import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { throwErrorMessage } from "../../../../helpers/kwikui.common.helpers";
import { DEFAULT_VALUES, VALIDATE_KEY_VALUES } from "./input-select.constants";
import { TKwikUIInputSelectSize } from "./input-select.definitions";
import { isValidKeyValue } from "./input-select.validation";

@Component({
  selector: "kwikui-input-select",
  templateUrl: "./input-select.component.html",
  styleUrls: ["./input-select.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KwikUIInputSelectComponent),
      multi: true
    }
  ]
})
export class KwikUIInputSelectComponent
  implements OnInit, OnChanges, ControlValueAccessor {
  @Input() disabled: boolean = DEFAULT_VALUES.disabled;

  @Input() focus: boolean = DEFAULT_VALUES.focus;

  @Input() formControl: FormControl = new FormControl({});

  @Input() formControlName: string = DEFAULT_VALUES.formControlName;

  @Input() hintContent: string = DEFAULT_VALUES.hintContent;

  @Input() icon: string = DEFAULT_VALUES.icon;

  @Input() id: string = DEFAULT_VALUES.id;

  @Input() invalid: boolean = DEFAULT_VALUES.invalid;

  @Input() label: string = DEFAULT_VALUES.label;

  @Input() options: Array<any> = DEFAULT_VALUES.options;

  @Input() placeholder: string = DEFAULT_VALUES.placeholder;

  @Input() properties = { readOnly: false };

  @Input() searchable: boolean = DEFAULT_VALUES.searchable;

  @Input() size: TKwikUIInputSelectSize = DEFAULT_VALUES.size;

  @Input() validators = { required: true };

  @Output() getKeyValue: EventEmitter<any> = new EventEmitter<any>();

  formGroup: FormGroup = new FormGroup({});

  subscriptions = new Subscription();

  constructor() {}

  ngOnInit(): void {
    if (this.options !== undefined && this.options !== null) {
      this.options = this.formatOptions(this.options);
    }
    this.formGroup.addControl(this.formControlName, this.formControl);

    this.focus = this.focus === undefined ? false : this.focus;
    this.invalid =
      this.invalid === undefined ? false : this.invalid && this.focus;
    this.validators = { ...this.validators };
    this.properties = { ...this.properties };

    this.setDisabled();

    this.subscriptions.add(
      this.formGroup.controls[this.formControlName].valueChanges
        .pipe(distinctUntilChanged()) // makes sure the value has actually changed.
        .subscribe((value) => this.handleValueChange(value))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const verifyChange = (key: string) => {
      return changes.hasOwnProperty(key) && !changes[key].firstChange;
    };

    for (const change of Object.entries(changes)) {
      const key: string = change[0];
      const value: any = change[1].currentValue;
      this.validateInputProperty(key, value);
    }

    if (verifyChange("formControl")) {
      this.formControl = changes.formControl.currentValue;
    }
    if (verifyChange("invalid")) {
      this.invalid = Boolean(changes.invalid.currentValue);
      this.setError();
    }
    if (verifyChange("focus")) {
      this.focus = Boolean(changes.focus.currentValue);
    }
    if (verifyChange("searchable")) {
      this.searchable = Boolean(changes.searchable.currentValue);
    }
    if (verifyChange("disabled")) {
      this.disabled = Boolean(changes.disabled.currentValue);
      this.setDisabled();
    }
    if (verifyChange("options")) {
      if (
        changes.options.currentValue !== undefined &&
        changes.options.currentValue !== null
      ) {
        this.options = this.formatOptions(changes.options.currentValue);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // ensure when component is destroyed the subscription is also and not left open.
  }

  private validateInputProperty(key: string, value: any): void {
    if (VALIDATE_KEY_VALUES[key] && !isValidKeyValue(key, value)) {
      this[key] = DEFAULT_VALUES[key];
      throwErrorMessage(
        "kwikui-input-select",
        this.id,
        key,
        value,
        DEFAULT_VALUES[key]
      );
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setDisabled() {
    if (this.formGroup.controls[this.formControlName] !== undefined) {
      if (this.disabled === true) {
        this.formGroup.controls[this.formControlName].disable({
          emitEvent: false
        });
      } else {
        this.formGroup.controls[this.formControlName].enable({
          emitEvent: false
        });
      }
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setError() {
    if (
      this.formGroup.controls[this.formControlName] !== undefined &&
      this.formGroup.controls[this.formControlName].invalid
    ) {
      this.invalid = true;
      this.focus = true;
    } else {
      this.invalid = false;
      this.focus = false;
    }
  }

  /**
   * @description Handles firing of 2 events on (keyup) event
   *
   * @param value
   */
  handleValueChange(value: any) {
    if (value === this.formControl.value) {
      this.emitEvent(this.getKeyValue, {
        key: this.formControlName,
        value: this.formControl.value
      });
    }
  }

  handleFocusedChange(e: any) {
    if (!this.invalid) {
      this.focus = e;
    }
  }

  formatOptions(
    options: Array<any>
  ): Array<string> | Array<number> | Array<boolean> {
    options = options.map((_) => {
      if (_.hasOwnProperty("title") && _.hasOwnProperty("value")) {
        return _.title;
      }
      if (
        typeof _ === "string" ||
        typeof _ === "number" ||
        typeof _ === "boolean"
      ) {
        return _;
      }
      return _;
    });

    return options;
  }

  emitEvent(event: any, data: any) {
    event.emit(data);
  }

  /** Method Implementations for Abstract Control */
  writeValue(value: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
